import {
  usePostQuestion,
  useRegenerateWisdomResponse,
} from '@modules/oracle/hooks';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { updatePromptByID } from '@modules/oracle/utils';
import { useCallback, useRef } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface GenerateWisdomResponseParams {
  onGenerate: () => void;
  question: string;
}

interface UseGenerateWisdomResponseOptions {
  editedFromResponseID?: string;
}

export const useGenerateWisdomResponse = ({
  editedFromResponseID,
}: UseGenerateWisdomResponseOptions = {}) => {
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const promptByID = useOracleSearchStore(({ data }) => data.promptByID);
  const currentPromptID = useOracleSearchStore(
    ({ data }) => data.currentPromptID,
  );
  const chatEditToken = useOracleSearchStore(
    ({ data }) => data.chat?.editToken,
  );

  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const dependencies = {
    currentPromptID,
    promptByID,
    editedFromResponseID,
    userID,
    tenantID,
  };

  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const [postQuestion, { isLoading: isPostQuestionLoading }] =
    usePostQuestion();

  const [regenerateResponse, { isLoading: isRegenerateResponseInProgress }] =
    useRegenerateWisdomResponse();

  const dispatchUpdatePromptByID = useCallback(
    (newPromptID: string, question: string) => {
      const updatedPromptByID = updatePromptByID({
        newPromptID,
        promptByID: dependenciesRef.current.promptByID ?? {},
        editedFromResponseID: dependenciesRef.current.editedFromResponseID,
        newPromptData: {
          question,
          userID: dependenciesRef.current.userID,
          tenantID: dependenciesRef.current.tenantID,
          previousResponseID: dependenciesRef.current.editedFromResponseID
            ? undefined
            : dependenciesRef.current.currentPromptID,
        },
      });

      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: {
          promptByID: updatedPromptByID,
          currentPromptID: newPromptID,
        },
      });
    },
    [dispatch],
  );

  const generateNewResponse = useCallback(
    async (question: string) => {
      const resp = await postQuestion({
        question,
        shouldCreateChat: !chatEditToken,
      });

      const newPromptID = resp?.data?.getAssistantResponse?.responseID;

      if (newPromptID) {
        dispatchUpdatePromptByID(newPromptID, question);
      }
      return newPromptID;
    },
    [chatEditToken, dispatchUpdatePromptByID, postQuestion],
  );

  const generateWisdomResponse = useCallback(
    async ({ question, onGenerate }: GenerateWisdomResponseParams) => {
      const editedPromptID = dependenciesRef.current.editedFromResponseID;

      if (editedPromptID) {
        await regenerateResponse({
          promptID: editedPromptID,
          question,
          onRegenerate: onGenerate,
        });
        return;
      }

      const newPromptID = await generateNewResponse(question);
      newPromptID && onGenerate();
    },
    [generateNewResponse, regenerateResponse],
  );

  return [
    generateWisdomResponse,
    {
      isLoading: isPostQuestionLoading || isRegenerateResponseInProgress,
    },
  ] as const;
};
